import React, { useMemo, useState } from 'react';
import { Form, FormControl } from 'react-bootstrap';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { DataVendor } from 'v2/models';
import { uploadFile } from 'v2/utils/fileUtils';
import restFactory from 'v2/utils/restFactory';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import AvatarInput from 'v2/form/AvatarInput/AvatarInput';
import defaultCompanyAvatar from 'v2/images/defaultCompanyAvatar.png';
import styles from 'v2/common/DataVendorModals/modal.module.scss';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditDataVendorModalProps {
  dataVendor: DataVendor;
  show: boolean;
  onClose: (dataVendor?: DataVendor) => void;
}

const EditDataVendorModal = ({
  dataVendor,
  show,
  onClose,
}: EditDataVendorModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(dataVendor.name || '');
  const [classification, setClassification] = useState(
    dataVendor.classification || ''
  );
  const [avatar, setAvatar] = useState(dataVendor.avatar_url || '');
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const isFormValid = useMemo(
    () => name && classification,
    [name, classification]
  );

  const { mutate, isLoading } = useMutation(
    async () =>
      await restFactory.patch<DataVendor>(
        `/dj/api/v1/data_vendor/${dataVendor.id}/`,
        {
          name,
          classification,
          avatar_url: avatar,
        }
      ),
    {
      onSuccess: (data: DataVendor) => {
        onClose(data);
      },
      onError: () => {
        toast.error(`Failed to update ${t(TranslationKeys.dataVendor_one)}`);
      },
    }
  );

  const handleAvatarChange = async (file: File | null) => {
    if (!file) {
      setAvatar('');
      return;
    }
    setIsAvatarLoading(true);
    const resp = await uploadFile(file);
    setAvatar(resp.url);
    setIsAvatarLoading(false);
  };

  const handleSubmit = async () => {
    if (!name || !classification) {
      return;
    }
    await mutate();
  };

  return (
    <ConfirmationModal
      show={show}
      title={`Edit ${t(TranslationKeys.dataProvider_one, {
        postProcess: PostProcessKeys.TitleCase,
      })}`}
      onOk={handleSubmit}
      okBtnDisabled={!isFormValid}
      okBtnText="Save"
      onCancel={() => onClose()}
      isLoading={isLoading}
      className={styles.viewModals}
      centered={true}
      animation={true}
    >
      <Form onSubmit={handleSubmit}>
        <div className="d-flex flex-row justify-content-between">
          <Form.Group
            className={classnames('flex-column', styles.formGroup)}
            controlId="name"
          >
            <Form.Label>
              {t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })}{' '}
              Name
            </Form.Label>
            <FormControl
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              isInvalid={!isFormValid && !name}
            />
            <FormControl.Feedback type="invalid">
              {t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })}{' '}
              name is required
            </FormControl.Feedback>
          </Form.Group>
          <Form.Group
            className={classnames('flex-column', styles.formGroup)}
            controlId="classification"
          >
            <Form.Label>
              {t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })}{' '}
              Type
            </Form.Label>
            <FormControl
              type="text"
              placeholder="Classification"
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              isInvalid={!isFormValid && !classification}
            />
            <FormControl.Feedback type="invalid">
              {t(TranslationKeys.company_one, {
                postProcess: PostProcessKeys.TitleCase,
              })}{' '}
              type is required
            </FormControl.Feedback>
          </Form.Group>
        </div>
        <Form.Group controlId="avatar">
          <AvatarInput
            defaultImage={defaultCompanyAvatar}
            addBtnText="Upload Photo"
            removeBtnText="Remove Photo"
            file={avatar}
            onChange={handleAvatarChange}
            removeBtnVisible={true}
            className={classnames(styles.avatarInput, 'mt-2', 'form-group')}
            isLoading={isAvatarLoading}
          />
        </Form.Group>
      </Form>
    </ConfirmationModal>
  );
};

export default EditDataVendorModal;
